// tanstack
import { Link, useLocation } from '@tanstack/react-router';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';

// material ui
import { Card, Tab, Tabs, Tooltip } from '@mui/material';

// components
import { servicesList } from '@/utils/services';

interface ServiceItem {
  Id: string | number;
  Name?: string;
}

interface ServiceListItem extends ServiceItem {
  path: string;
}

interface ServiceGroup {
  key: string;
  path: string;
  label: string;
  serviceIds: Array<string | number>;
}

interface Account {
  Services?: ServiceItem[];
}

interface ServiceTabsProps {
  account?: Account;
  orientation?: 'horizontal' | 'vertical';
  accountId: string;
  selectedAccountId?: string;
  routePrefix?: 'service-manager' | 'account/services';
}

const ServiceTabs = ({
  account,
  orientation = 'horizontal',
  accountId,
  selectedAccountId,
  routePrefix = 'service-manager',
}: ServiceTabsProps) => {
  const location = useLocation();
  const isVertical = orientation === 'vertical';
  const targetAccountId = selectedAccountId ?? accountId;

  const serviceGroupsMap = (servicesList as ServiceListItem[]).reduce(
    (groups, service) => {
      const groupKey = service.path;
      const groupLabel =
        groupKey === 'basic-dashboards' ? 'Basic Dashboards' : service.Name;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          path: service.path,
          label: groupLabel,
          serviceIds: [service.Id],
        });
        return groups;
      }

      groups.get(groupKey)?.serviceIds.push(service.Id);
      return groups;
    },
    new Map<string, ServiceGroup>(),
  );

  const groupedServices = Array.from(serviceGroupsMap.values());

  const hasServiceGroup = (serviceIds: Array<string | number>) =>
    !!account?.Services?.some((service) => serviceIds.includes(service.Id));

  const getCurrentTab = (): number | false => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    const currentTabIndex = groupedServices.findIndex(
      (serviceGroup) =>
        pathSegments.includes(serviceGroup.path) ||
        serviceGroup.path === lastSegment,
    );

    return currentTabIndex !== -1 ? currentTabIndex : false;
  };

  return (
    <Card>
      <Tabs
        orientation={orientation}
        value={getCurrentTab()}
        aria-label="service tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTabs-indicator': {
            height: isVertical ? 'auto' : 3,
            width: isVertical ? 3 : 'auto',
            left: isVertical ? 0 : 'auto',
            backgroundColor: 'brand.main',
            borderRadius: isVertical ? '0 3px 3px 0' : '3px 3px 0 0',
          },
          '& .MuiTab-root': {
            minHeight: 48,
            justifyContent: isVertical ? 'space-between' : 'center',
            alignItems: isVertical ? 'center' : 'center',
            transition: 'opacity 0.2s ease',
          },
        }}
      >
        {groupedServices.map((serviceGroup) => (
          <Tooltip
            key={serviceGroup.key}
            placement="left"
            title={
              hasServiceGroup(serviceGroup.serviceIds)
                ? 'The account has access to this feature. When a user with access to this account, they will see this feature in the left navigation menu.'
                : 'The account does not have access to this feature. When a user with access to this account, they will not see this feature in the left navigation menu.'
            }
          >
            <Tab
              key={serviceGroup.key}
              label={serviceGroup.label}
              icon={
                hasServiceGroup(serviceGroup.serviceIds) ? (
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 18,
                      color: 'success.main',
                    }}
                  />
                ) : (
                  <VisibilityOffRoundedIcon
                    sx={{
                      fontSize: 18,
                      color: 'text.disabled',
                    }}
                  />
                )
              }
              iconPosition="end"
              component={Link}
              to={
                routePrefix === 'service-manager'
                  ? `/${accountId}/service-manager/${targetAccountId}/${serviceGroup.path}`
                  : `/${accountId}/account/services/${serviceGroup.path}`
              }
              sx={{
                textTransform: 'none',
                alignItems: isVertical ? 'center' : 'center',
                justifyContent: isVertical ? 'space-between' : 'center',
                maxWidth: isVertical ? 'none' : 'unset',
                width: isVertical ? '100%' : 'auto',

                "& .MuiTab-icon": {
                  marginLeft: 1,
                  marginRight: 0,
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'primary.main',
                },
              }}
            />
          </Tooltip>
        ))}
      </Tabs>
    </Card>
  );
};

export default ServiceTabs;
